/* eslint-disable */
// rsa
import * as jsrsasign from 'jsrsasign';

// dates
import moment from 'moment-timezone';

// language
import {languageDetector} from '@/helpers/language';

/************
 * CER FILE *
 ************/

export const handleReadCerToPem = async (file) => {
	try {
		return await __readUploadedCERFileAsPEM(file);
	} catch (e) {
		return e;
	}
};

const __readUploadedCERFileAsPEM = (inputFile) => {
	const temporaryFileReader = new FileReader();

	return new Promise((resolve, reject) => {
		temporaryFileReader.onerror = () => {
			temporaryFileReader.abort();
			reject('Error: reading cer file');
		};

		temporaryFileReader.onload = () => {
			const certHex = jsrsasign.ArrayBuffertohex(temporaryFileReader.result);
			let certPem = jsrsasign.hextorstr(certHex);

			if (!certPem.startsWith('-----BEGIN CERTIFICATE-----')) certPem = jsrsasign.KJUR.asn1.ASN1Util.getPEMStringFromHex(certHex, 'CERTIFICATE');

			resolve(certPem);
		};

		temporaryFileReader.readAsArrayBuffer(inputFile);
	});
};

export const getCerInfo = async (certPem) => {
	return await __getCerInfo(certPem)
		.then((cerInfo) => {
			return cerInfo;
		})
		.catch((reason) => {
			return reason;
		});
};

const __getCerInfo = (certPem) => {
	let certificate = new jsrsasign.X509();
	certificate.readCertPEM(certPem);
	const pubKey = certificate.getPublicKey();
	const pubKeyHex = pubKey.n.toString(16);

	let pki = require('node-forge').pki;
	const cert = pki.certificateFromPem(certPem);
	const certValidity = cert.validity;

	//console.log(certificate);
	//console.log(cert);

	/*const asn1Cert = pki.certificateToAsn1(cert);
    console.log('asn1:', asn1Cert);*/

	return new Promise((resolve, reject) => {
		const info = {
			validFrom: certValidity.notBefore,
			validTo: certValidity.notAfter,
			pubKey,
			pubKeyHex,
			msg: 'OK',
		};

		if (typeof info.validFrom !== 'object' || typeof info.validTo !== 'object' || typeof info.pubKey !== 'object' || typeof info.pubKeyHex !== 'string') {
			reject({msg: 'Error: Unable to get cert info'});
		}

		resolve(info);
	});
};

/************
 * KEY FILE *
 ************/

export const handleReadKeyToPem = async (file) => {
	try {
		return await __readUploadedKEYFileAsPEM(file);
	} catch (e) {
		return e;
	}
};

const __readUploadedKEYFileAsPEM = (inputFile) => {
	const temporaryFileReader = new FileReader();

	return new Promise((resolve, reject) => {
		temporaryFileReader.onerror = () => {
			temporaryFileReader.abort();
			reject('Error reading key file');
		};

		temporaryFileReader.onload = () => {
			const prvp8hex = jsrsasign.ArrayBuffertohex(temporaryFileReader.result);
			const prvp8pem = jsrsasign.KJUR.asn1.ASN1Util.getPEMStringFromHex(prvp8hex, 'ENCRYPTED PRIVATE KEY');

			resolve(prvp8pem);
		};

		temporaryFileReader.readAsArrayBuffer(inputFile);
	});
};

export const getKeyInfo = async (prvp8pem, passphrase) => {
	return await __getKeyInfo(prvp8pem, passphrase)
		.then((prvp8Info) => {
			return prvp8Info;
		})
		.catch((reason) => {
			return reason;
		});
};

const __getKeyInfo = (prvp8pem, passphrase = null) => {
	if (passphrase) {
		return new Promise((resolve, reject) => {
			try {
				const prvKey = jsrsasign.KEYUTIL.getKey(prvp8pem, passphrase);
				const prvKeyHex = prvKey.n.toString(16);

				const info = {
					prvKey,
					prvKeyHex,
					msg: 'OK',
				};

				if (typeof info.prvKey !== 'object' || typeof info.prvKeyHex !== 'string') {
					reject({msg: 'Error: Unable to get key info'});
				}

				resolve(info);
			} catch (e) {
				reject({msg: `${e}`});
			}
		});
	}

	// not passphrase
	return new Promise((resolve, reject) => {
		resolve({
			prvKey: null,
			prvKeyHex: null,
			msg: 'No passphrases set yet',
		});
	});
};

/***********
 * GENERAL *
 ***********/

export const getFileExtension = (filename) => {
	return filename.split('.').pop();
};

/************
 * VALIDATE *
 ************/

export const validateFIEL = async (certPem = null, prvp8pem = null, passphrase = '') => {
	return await __validateFIEL(certPem, prvp8pem, passphrase)
		.then((result) => {
			return result;
		})
		.catch((reason) => {
			return reason;
		});
};

const __validateFIEL = (certPem = null, prvp8pem = null, passphrase = '') => {
	if (certPem === null || prvp8pem === null || passphrase === '')
		return new Promise((resolve, reject) => {
			resolve({
				isValid: false,
				error: 'notEnoughData',
				certDate: null,
			});
		});

	return new Promise((resolve, reject) => {
		Promise.all([getCerInfo(certPem), getKeyInfo(prvp8pem, passphrase)])
			.then((values) => {
				//console.log(values);

				const certInfo = values[0];
				if (certInfo.msg.includes('Error')) {
					reject({
						isValid: false,
						error: 'invalidCert',
						certDate: null,
					});
				}

				// validacion de fechas
				moment.locale(languageDetector());
				const now = moment().tz('America/Mexico_City');
				const certValidFrom = moment(certInfo.validFrom).tz('America/Mexico_City');
				const certValidTo = moment(certInfo.validTo).tz('America/Mexico_City');
				if (!now.isBetween(certValidFrom, certValidTo)) {
					reject({
						isValid: false,
						error: 'invalidCertDate',
						certDate: moment(certInfo.validTo).tz('America/Mexico_City').format('DD-MMM-YYYY').replace('.', ''),
					});
				}

				const keyInfo = values[1];
				if (keyInfo.msg.includes('Error')) {
					if (keyInfo.msg.includes('malformed plain PKCS8 private key')) {
						reject({
							isValid: false,
							error: 'invalidPassphrase',
							certDate: null,
						});
					}

					reject({
						isValid: false,
						error: 'invalidKey',
						certDate: null,
					});
				}

				if (certInfo.pubKeyHex !== keyInfo.prvKeyHex) {
					reject({
						isValid: false,
						error: 'notMatchKeyWithCert',
						certDate: null,
					});
				}

				resolve({
					isValid: true,
					error: null,
					certDate: moment(certInfo.validTo).tz('America/Mexico_City').format('DD-MMM-YYYY').replace('.', ''),
				});
			})
			.catch((reason) => {
				// imprimimos el error para debug
				console.error('Ocurrio un error: ', reason);
				reject({
					isValid: false,
					error: 'generalError',
					certDate: null,
				});
			});
	});
};
